import React, { ReactNode, useRef, useState } from 'react';
import { ModalsKeys } from 'global';
import { logError } from 'utils/logger';
import { useModal } from 'hooks/useModal';
import { Loop } from '@mui/icons-material';
import { useInvoice } from 'modules/invoice';
import { useNotifications } from 'modules/notification';
import { useHistory, useParams } from 'react-router-dom';
import { capitalize, Stack, Button } from '@mui/material';
import { ListingForm } from 'components/shared/TableListing/types';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { CardTitle } from 'components/shared/CardContainer/CardTitle';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { collectInvoiceDTOFromQuote, mapQuoteEquipmentToSync } from 'modules/quote/lib';
import { CalculatedTotalsByLevel, QuoteChargeListingEntity, QuoteChargeType } from 'modules/quote/types';
import {
  useQuote,
  QuoteListingLoading,
  InvoiceDetailsListing,
  NetFinanceAmountListing,
  collectQuoteFromInvoiceForm,
} from 'modules/quote';
import {
  useVOEquipment,
  CreateChargesDialog,
  useEquipmentActions,
  EquipmentChargesDialog,
  CreateUpdateEquipmentDialog,
  UploadImagesForNewEquipmentDialog,
  UploadImagesForExistingEquipmentDialog,
  PreviewExistingImagesDialog,
} from 'modules/equipment';

const modals: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.ChargesEquipmentDialog]: <EquipmentChargesDialog />,
  [ModalsKeys.CreateChargesEquipmentDialog]: <CreateChargesDialog />,
  [ModalsKeys.CreateUpdateEquipmentDialog]: <CreateUpdateEquipmentDialog />,
  [ModalsKeys.UploadImagesForNewEquipmentDialog]: <UploadImagesForNewEquipmentDialog />,
  [ModalsKeys.UploadImagesForExistingEquipmentDialog]: <UploadImagesForExistingEquipmentDialog />,
  [ModalsKeys.PreviewExistingEquipmentImagesDialog]: <PreviewExistingImagesDialog />,
};

export const QuotePage = () => {
  const { handleOpen, handleClose } = useModal(ModalsKeys.Confirmation);

  const chargesSubmitRef = useRef<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>(null);

  const [calculatedTotalsByLevel, setCalculatedTotalsByLevel] = useState<CalculatedTotalsByLevel>({
    invoice: { fees: 0, taxes: 0, price: 0 },
    equipments: { fees: 0, taxes: 0, price: 0 },
    attachments: { fees: 0, taxes: 0, price: 0 },
  });

  const history = useHistory();
  const { VOId, dynamicsVOId } = useParams<{ VOId: string; dynamicsVOId: string }>();
  const { showNotification } = useNotifications();

  const { handleUpdateInvoice, isInvoiceUpdating, handleApplyFeesAndTaxes, isApplyingFeesAndTaxes } = useInvoice();
  const { quote, isQuoteLoading, handleCreateOrUpdateQuote, isQuoteCreatingOrUpdating, quoteAPI } = useQuote(VOId);
  const { isLoading, equipments } = useVOEquipment(VOId);
  const { onUpdateEquipment, isEquipmentUpdating } = useEquipmentActions();

  const handleGoToVO = () => {
    history.push(`/vo/${VOId}`);
  };

  const handleSubmitAll = async () => {
    try {
      const valuesCharges = chargesSubmitRef.current?.submit()?.bodyFields ?? [];

      const quoteDTO = collectQuoteFromInvoiceForm(valuesCharges);
      await handleCreateOrUpdateQuote(VOId, quoteDTO);

      const listOfEquipmentDTO = valuesCharges
        .filter(({ type }) => type === QuoteChargeType.Equipment)
        .map(({ equipment }) => ({
          ...mapQuoteEquipmentToSync(equipment!),
          vendorOpportunityId: VOId,
          equipmentId: equipment!.equipmentId,
          dynamicsVendorOpportunityId: dynamicsVOId,
        }));

      await Promise.all(listOfEquipmentDTO.map(dto => onUpdateEquipment(dto)));

      const invoiceDTO = collectInvoiceDTOFromQuote(quoteDTO);
      await handleUpdateInvoice({ VOId, entityId: dynamicsVOId, fieldsObj: invoiceDTO });

      showNotification('Successfully Updated!');

      handleGoToVO();
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const saveLoading = isQuoteCreatingOrUpdating || isEquipmentUpdating || isInvoiceUpdating;

  return (
    <PageContainer modals={modals} title="Quote">
      <CardContainer
        title={<CardTitle label={`${capitalize(VOId ? 'edit' : 'add')} Invoice Detail`} onRedirect={handleGoToVO} />}
      >
        <Stack gap="1rem">
          <RenderWithFallback isLoading={isLoading || isQuoteLoading} fallBack={<QuoteListingLoading />}>
            <InvoiceDetailsListing
              VOId={VOId}
              quote={quote}
              equipmentList={equipments}
              dynamicsVOId={dynamicsVOId}
              chargesSubmitRef={chargesSubmitRef}
              onSubscribeTotals={setCalculatedTotalsByLevel}
            />

            <NetFinanceAmountListing {...calculatedTotalsByLevel} />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <RenderWithCondition condition={!!equipments.length}>
                <Button
                  startIcon={<Loop />}
                  loading={isApplyingFeesAndTaxes}
                  onClick={() => {
                    handleOpen({
                      title: 'Confirmation',
                      message:
                        'Warning - all existing taxes, fees, charges, and discounts on this invoice and associated equipment will be removed and replaced. Continue?',
                      onConfirm: async () => {
                        handleClose();
                        await handleApplyFeesAndTaxes(VOId, () => ({
                          refetchQueries: [quoteAPI.getQuote],
                          awaitRefetchQueries: true,
                        }));
                      },
                    });
                  }}
                >
                  Apply Taxes and Fees
                </Button>
              </RenderWithCondition>

              <Stack direction="row" gap="1rem">
                <Button disabled={saveLoading} onClick={handleGoToVO}>
                  Cancel
                </Button>

                <Button loading={saveLoading} variant="contained" onClick={handleSubmitAll}>
                  Save changes
                </Button>
              </Stack>
            </Stack>
          </RenderWithFallback>
        </Stack>
      </CardContainer>
    </PageContainer>
  );
};
